import { Box, BoxProps, styled } from "@mui/material";
import { rem } from "polished";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";

import { ariaLabel as ariaLabelConst } from "src/constants";
import lazyWithRetry from "src/containers/LazyWithRetry";
import { SortDirectionEnum, PageInfoFragment, SortByType } from "src/graphql/generated/types";
import { useDeviceWidth } from "src/hooks";

import { Card } from "../Card";
import ResponsiveTableContainer from "../ResponsiveTableContainer";
import { ListDefaultHeader } from "./_parts/DefaultHeader";
import { ListDefaultRow } from "./_parts/DefaultRow";
import { ListFetchMore } from "./_parts/FetchMore";
import { ListPaginator } from "./_parts/Paginator";
import { ListTable } from "./_parts/Table";

const ResponsiveList = lazyWithRetry(() => import("./_parts/ListResponsive"));

export type ListSingleColumnConfig<T, U> = {
  type?: "selection" | "expand"; // Special for component internal need
  label: string | React.ReactElement;
  width?: number;
  headerAlign?: "left" | "center" | "right";
  rowAlign?: "left" | "center" | "right";
  render: (data: T, extraProps: U) => any;
  // better
  sortableKey?: string;
};

const selectionColumn: ListSingleColumnConfig<any, any> = {
  type: "selection",
  label: "", // Overide by _type
  width: 5,
  headerAlign: "center",
  rowAlign: "center",
  render: () => null, // Overide by _type
};

const expandColumn: ListSingleColumnConfig<any, any> = {
  type: "expand",
  label: "", // Overide by _type
  width: 5,
  headerAlign: "center",
  rowAlign: "center",
  render: () => null, // Overide by _type
};

export type ListColumnsConfig<T, U = any> = Array<ListSingleColumnConfig<T, U>>;

type Props<T, U> = {
  columns: ListColumnsConfig<T, U>;
  data: Array<T>;
  dataType?: string;
  dataKey?: string;
  extraProps?: U;
  options?: React.ReactElement;
  onRowClick?: (data: T) => void;
  emptyListText?: string;
  canFetchMore?: boolean; // @note: use for infinite pagination
  isFetchingNextPage?: boolean;
  onFetchMore?: () => void; // @note: use for infinite pagination
  expandComponent?: FC<{ data: T; extraProps: U }>;
  maxSelectedItems?: number;
  onSelectedItemsChange?: (selectedItems: Array<T>) => void;
  settingVariables?: boolean;
  paginationInfo?: PageInfoFragment; // @note: use for basic pagination
  onPageChange?: (newPageNumber: number) => void; // @note: use for basic pagination
  ResponsiveView?: FC<T>;
  isRowList?: boolean;
  // Sorting
  activeSort?: SortByType;
  onSort?: (newSort: SortByType) => void;
  defaultSortDirection?: SortDirectionEnum; // Default : ASC
  // Accessibility
  ariaLabelledby?: string;
  ariaLabel?: string;
  role?: string;
  id?: string;
} & BoxProps;

const StyledRoot = styled(Box)`
  position: relative;
`;

const StyledSettingVariablesOverlay = styled("div")`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(255, 255, 255, 0.7);
  z-index: 110;
`;

const StyledEmptyListText = styled(Card)`
  text-align: center;
  color: ${(props) => props.theme.palette.text.primary};
  font-size: ${rem(16)};

  ${(props) => props.theme.breakpoints.down("sm")} {
    height: ${rem(50)};
  }
`;

const StyledParagraph = styled("p")`
  margin: 0;
`;

export function List<T extends { [key: string]: any }, U = undefined>({
  data,
  dataType,
  dataKey = "id",
  columns,
  extraProps,
  options,
  onRowClick,
  emptyListText = "Aucune donnée",
  canFetchMore,
  isFetchingNextPage,
  onFetchMore,
  expandComponent,
  maxSelectedItems = 5,
  onSelectedItemsChange,
  settingVariables = false,
  paginationInfo,
  onPageChange,
  ResponsiveView,
  isRowList,
  // Sorting
  activeSort,
  onSort,
  defaultSortDirection = SortDirectionEnum.Asc,
  // Accessibility
  ariaLabelledby,
  ariaLabel,
  id,
  role,
  // Rest props
  ...boxProps
}: Props<T, U>): React.ReactElement {
  const [selectedItems, setSelectedItems] = useState<{
    [key: string]: { isSelected: boolean };
  }>({});
  const [wasFetchingMore, setWasFetchingMore] = useState(false);
  const { isTabletMobile } = useDeviceWidth();

  const selectedCount = useMemo(
    () => Object.values(selectedItems).filter((value) => value.isSelected).length,
    [selectedItems]
  );

  const toggleAll = useCallback(
    (checked: boolean) => {
      if (!checked) {
        setSelectedItems({});
      } else {
        const newState = data.reduce((acc, d) => {
          acc[d[dataKey]] = {
            isSelected: true,
          };
          return acc;
        }, {} as { [key: string]: { isSelected: boolean } });
        setSelectedItems(newState);
      }
    },
    [data, dataKey]
  );

  const toggleRow = useCallback(
    (rawData: T, checked: boolean) => {
      setSelectedItems((currentState) => {
        const key = rawData[dataKey];
        return {
          ...currentState,
          [key]: {
            isSelected: checked,
            data: rawData,
          },
        };
      });
    },
    [dataKey]
  );

  useEffect(() => {
    if (onSelectedItemsChange) {
      onSelectedItemsChange(data.filter((d) => selectedItems[d[dataKey]]?.isSelected));
    }
  }, [data, selectedItems, onSelectedItemsChange]);

  const finalColumnsConfig = useMemo(() => {
    if (onSelectedItemsChange) {
      return [selectionColumn, ...columns];
    }
    if (expandComponent) {
      return [expandColumn, ...columns];
    }
    return columns;
  }, [columns, onSelectedItemsChange, expandComponent]);

  const handlePageChange = (newPageNumber: number) => {
    if (onPageChange) {
      onPageChange(newPageNumber);
      document.getElementById(`${id}`)?.focus();
    }
  };

  const handleFetchMore = useCallback(() => {
    if (onFetchMore) {
      setWasFetchingMore(true);
      onFetchMore();
    }
  }, [onFetchMore]);

  useEffect(() => {
    if (data.length > 10 && wasFetchingMore) {
      const firstNewRowIndex = data.length - 1;
      const firstNewRowId = `list-row-${firstNewRowIndex}`;

      setTimeout(() => {
        const element = document.getElementById(firstNewRowId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          element.focus();
        }
      }, 100);
    }
  }, [data.length, wasFetchingMore]);

  const listTableComponent = (
    <ListTable
      aria-labelledby={ariaLabelledby}
      id={id}
      tabIndex={-1}
      sx={{
        "& :focus-visible": {
          outline: "none",
        },
      }}
      aria-label={ariaLabel}
      role={role || (expandComponent && "treegrid")}
    >
      <thead aria-label={ariaLabelConst.listHeader}>
        <ListDefaultHeader
          isMaxSelectedReached={data.length > maxSelectedItems}
          columnsConfig={finalColumnsConfig}
          toggleAll={toggleAll}
          areAllRowSelected={data.length === selectedCount}
          activeSort={activeSort}
          onSort={onSort}
          defaultSortDirection={defaultSortDirection}
        />
        {options}
      </thead>
      <tbody>
        {data.map((value, index) => {
          const rowKey = value[dataKey];
          return (
            <ListDefaultRow
              key={rowKey}
              rowIndex={index}
              data={value}
              dataType={dataType}
              columnsConfig={finalColumnsConfig}
              isSelected={Boolean(selectedItems[rowKey]?.isSelected)}
              isMaxSelectedReached={selectedCount >= maxSelectedItems}
              onToggle={toggleRow}
              onRowClick={onRowClick}
              extraProps={extraProps}
              expandComponent={expandComponent}
            />
          );
        })}
      </tbody>
    </ListTable>
  );

  const mobileList = ResponsiveView ? (
    <ResponsiveList>
      <ResponsiveTableContainer minWidth="fit-content">
        <table
          aria-labelledby={ariaLabelledby}
          aria-label={ariaLabel}
          role={role}
          id={id}
          tabIndex={-1}
          style={{ outline: "none !important" }}
        >
          <thead aria-label={ariaLabelConst.listHeader}>{options}</thead>
        </table>
      </ResponsiveTableContainer>
      {data.map((value) => {
        const rowKey = value[dataKey];
        return (
          <ResponsiveView key={rowKey} onClick={() => onRowClick && onRowClick(value)} {...value} />
        );
      })}
    </ResponsiveList>
  ) : (
    <ResponsiveTableContainer>{listTableComponent}</ResponsiveTableContainer>
  );

  return (
    <StyledRoot {...boxProps} sx={{ ...boxProps.sx, maxWidth: !isRowList ? "100vw" : undefined }}>
      {settingVariables && <StyledSettingVariablesOverlay />}

      {isTabletMobile && !isRowList ? mobileList : listTableComponent}

      {!data.length && (
        <StyledEmptyListText>
          <StyledParagraph>{emptyListText}</StyledParagraph>
        </StyledEmptyListText>
      )}

      {canFetchMore && onFetchMore && (
        <ListFetchMore
          data-cy="list-fetch-more"
          onClick={handleFetchMore}
          isLoading={isFetchingNextPage}
        />
      )}

      {paginationInfo && onPageChange && (
        <ListPaginator
          data-cy="list-paginator"
          onPageChange={handlePageChange}
          pageInfo={paginationInfo}
        />
      )}
    </StyledRoot>
  );
}
